import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Fade,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import StarIcon from "@mui/icons-material/Star";
import { Howl } from "howler";

const buttonClickSound = new Howl({
  src: ["/assets/audio/click-button.ogg", "/assets/audio/click-button.mp3"],
  volume: 0.2,
});

const CalendarArchive = ({
  open,
  onClose,
  gameKey,
  getGameDataForDate,
  formatDateForStorage,
  title = "Game Archive",
  description = "View your past games and achievements",
  onDateSelect,
  currentDate,
  showGoal = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [displayDate, setDisplayDate] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState(null);

  // Reset display date when modal opens
  useEffect(() => {
    if (open) {
      setDisplayDate(new Date());
    }
  }, [open]);

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const changeMonth = (increment) => {
    buttonClickSound.play();
    const newDate = new Date(displayDate);
    newDate.setMonth(newDate.getMonth() + increment);
    setDisplayDate(newDate);
  };

  // Use currentDate from props to determine 'Current' day
  const isCurrent = (date) => {
    if (!currentDate) return false;
    const cur = new Date(currentDate);
    cur.setHours(0, 0, 0, 0);
    const cellDate = new Date(date);
    cellDate.setHours(0, 0, 0, 0);
    return cellDate.getTime() === cur.getTime();
  };

  const getGameStatus = (date) => {
    const dateKey = formatDateForStorage(date);
    return getGameDataForDate(dateKey);
  };

  const isFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  };

  const handleDateClick = (date) => {
    console.log("CalendarArchive: handleDateClick called with date:", date);
    if (!isFutureDate(date)) {
      console.log(
        "CalendarArchive: Playing click sound and calling onDateSelect"
      );
      buttonClickSound.play();
      onDateSelect(date);
      onClose();
    } else {
      console.log("CalendarArchive: Date is in the future, ignoring click");
    }
  };

  const renderCalendarGrid = () => {
    console.log("CalendarArchive: Rendering calendar grid");
    const daysInMonth = getDaysInMonth(displayDate);
    const firstDay = getFirstDayOfMonth(displayDate);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<Box key={`empty-${i}`} sx={{ aspectRatio: "1" }} />);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        displayDate.getFullYear(),
        displayDate.getMonth(),
        day
      );
      const gameStatus = getGameStatus(date);
      const isFuture = isFutureDate(date);

      // Both completed and current: green fill + black border
      const bothCompletedAndCurrent = isCurrent(date) && gameStatus;

      // Dummy goal value for now
      const GOAL_MOVES = 17;
      const metGoal = showGoal && gameStatus && gameStatus.moves <= GOAL_MOVES;

      days.push(
        <Tooltip
          key={day}
          title={
            isFuture
              ? "Future puzzle"
              : gameStatus
              ? `${gameStatus.moves} moves on ${date.toLocaleDateString()}${
                  metGoal ? " (Goal met!)" : ""
                }`
              : "No game played"
          }
          placement="top"
        >
          <Box
            component={motion.div}
            whileHover={
              !isFuture && (gameStatus || isCurrent(date)) ? { scale: 1.1 } : {}
            }
            whileTap={
              !isFuture && (gameStatus || isCurrent(date))
                ? { scale: 0.95 }
                : {}
            }
            onClick={() => {
              console.log("CalendarArchive: Day clicked:", day, "Date:", date);
              if (!isFuture) {
                handleDateClick(date);
              } else {
                console.log("CalendarArchive: Click ignored - future date");
              }
            }}
            sx={{
              aspectRatio: "1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              cursor: !isFuture ? "pointer" : "default",
              minWidth: { xs: "32px", sm: "44px" },
              minHeight: { xs: "32px", sm: "44px" },
              ...(metGoal
                ? {
                    borderRadius: "50%",
                    backgroundColor: "transparent",
                    border: "none",
                  }
                : bothCompletedAndCurrent
                ? {
                    borderRadius: "50%",
                    backgroundColor: "#E8F5E9",
                    border: "2px solid #222",
                  }
                : isCurrent(date)
                ? {
                    borderRadius: "50%",
                    backgroundColor: "transparent",
                    border: "2px solid #222",
                  }
                : gameStatus
                ? {
                    borderRadius: "50%",
                    backgroundColor: "#E8F5E9",
                    border: "none",
                  }
                : {
                    backgroundColor: "transparent",
                    border: "none",
                  }),
            }}
          >
            {metGoal ? (
              <StarIcon
                sx={{ color: "#FFD700", fontSize: isMobile ? 28 : 32 }}
              />
            ) : (
              <Typography
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                  fontWeight: 600,
                  color: bothCompletedAndCurrent
                    ? "#4CAF50"
                    : isCurrent(date)
                    ? "#222"
                    : gameStatus
                    ? "#4CAF50"
                    : isFuture
                    ? "rgba(0, 0, 0, 0.3)"
                    : "text.primary",
                }}
              >
                {day}
              </Typography>
            )}
          </Box>
        </Tooltip>
      );
    }

    return days;
  };

  if (!open) return null;

  return (
    <Fade in={open} timeout={300}>
      <Box
        onClick={() => {
          buttonClickSound.play();
          onClose();
        }}
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          component={motion.div}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: "90%",
            maxWidth: "400px",
            p: { xs: 1.5, sm: 3 },
            borderRadius: 2,
            bgcolor: "white",
            boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <IconButton onClick={() => changeMonth(-1)}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {displayDate.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </Typography>
            <IconButton onClick={() => changeMonth(1)}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Calendar Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 1,
              mb: 2,
              overflowX: "auto",
              minWidth: { xs: "224px", sm: "100%" }, // 32px * 7 columns for xs
              width: "100%",
              maxWidth: "100%",
            }}
          >
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <Typography
                key={day}
                sx={{
                  textAlign: "center",
                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                  color: "text.secondary",
                  fontWeight: 500,
                  minWidth: { xs: "32px", sm: "unset" },
                }}
              >
                {day}
              </Typography>
            ))}
            {renderCalendarGrid()}
          </Box>

          {/* Legend */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mt: 2,
              pt: 2,
              borderTop: "1px solid rgba(0, 0, 0, 0.1)",
            }}
          >
            {/* Goal Met: star */}
            {showGoal && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <StarIcon sx={{ color: "#FFD700", fontSize: 20 }} />
                <Typography variant="caption">Goal Met</Typography>
              </Box>
            )}
            {/* Completed: light green fill */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  bgcolor: "#E8F5E9",
                  border: "none",
                  display: "inline-block",
                }}
              />
              <Typography variant="caption">Completed</Typography>
            </Box>
            {/* Current: black border, no fill */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  bgcolor: "transparent",
                  border: "2px solid #222",
                  display: "inline-block",
                }}
              />
              <Typography variant="caption">Current</Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};

export default CalendarArchive;
